/**
 * Demo-only "Show as someone else" card.
 *
 * Toggles a *display-only* persona overlay on the current session. The
 * authenticated user's identity (JWT, userID, role, email, permissions)
 * is unchanged — only the visible name + avatar in the sidebar account
 * chip and other places that read `state.auth.value.name` /
 * `state.auth.value.avatar` are masked.
 *
 * The previous implementation seeded three users into mongo with the
 * real MG employees' email addresses (`ronnie@puntt.ai`, etc.) and
 * authenticated against a shared bcrypt hash. That collided with the
 * `users.email_1` unique index and broke `Reset Demo`, plus the demo
 * password was a usable credential against real Kevin's address.
 * Background: see migration 0080 + `utils/demoPersona.ts`.
 *
 * Now: localStorage-backed override, no backend involvement, cleared on
 * `Reset Demo` / logout / explicit "Stop pretending" click.
 *
 * Gated behind `isDemoEnterprise()` at the call site so the card never
 * renders on a real tenant.
 */
import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { setDemoPersona } from "../../../utils/slices/auth";

interface PersonaOption {
  id: "ronnie" | "steph" | "kj";
  displayName: string;
  title: string;
  avatar: string;
  blurb: string;
}

// Avatars are name-initials only — we deliberately do NOT host real
// photos for these personas, because the whole point of the rewrite is
// "no real-employee data shipped with the seed". The AvatarWithInitials
// fallback path renders a clean colored circle with the persona's
// initials, which reads as "Ronnie" or "KJ" in the sidebar without
// implying an actual photo identity.
const PERSONAS: PersonaOption[] = [
  {
    id: "ronnie",
    displayName: "Ronnie",
    title: "Head of Marketing",
    avatar: "",
    blurb: "Pretend to be Ronnie for the rest of the demo.",
  },
  {
    id: "steph",
    displayName: "Steph",
    title: "Head of Marketing",
    avatar: "",
    blurb: "Pretend to be Steph for the rest of the demo.",
  },
  {
    id: "kj",
    displayName: "KJ",
    title: "Head of Marketing",
    avatar: "",
    blurb: "Pretend to be KJ for the rest of the demo.",
  },
];

export function DemoImpersonationCard() {
  const { t } = useTranslation("ui");
  const dispatch = useAppDispatch();
  const activeName = useAppSelector((state) => state.auth.value?.name ?? null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const isPersonaActive = (persona: PersonaOption) =>
    activeName === persona.displayName;

  function applyPersona(persona: PersonaOption) {
    setPendingId(persona.id);
    dispatch(
      setDemoPersona({
        name: persona.displayName,
        avatar: persona.avatar,
        title: persona.title,
      }),
    );
    setPendingId(null);
  }

  function clearPersona() {
    dispatch(setDemoPersona(null));
  }

  const anyPersonaActive = PERSONAS.some(isPersonaActive);

  return (
    <Card data-testid="settings-card-demo-impersonation">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            {t("settings.demoImpersonationCard.pretendSomeoneElse")}
          </Heading>
          <Text size="2" color="gray">
            Changes the name and avatar shown for you during this demo session
            so it looks like you&apos;re logged in as Ronnie, Steph, or KJ. Your
            real account is not touched. Comments you leave and any new activity
            will show the pretend name. The pretend mode clears automatically
            when you click{" "}
            <Text weight="bold">
              {t("settings.demoImpersonationCard.resetDemo")}
            </Text>{" "}
            or log out.
          </Text>
        </Flex>
        <Flex direction="column" gap="2">
          {PERSONAS.map((persona) => {
            const active = isPersonaActive(persona);
            const isPending = pendingId === persona.id;
            return (
              <Flex key={persona.id} align="center" gap="3" wrap="wrap">
                <Button
                  data-testid={`settings-demo-impersonate-${persona.id}`}
                  onClick={() => applyPersona(persona)}
                  disabled={isPending || active}
                  variant={active ? "soft" : "solid"}
                >
                  {active
                    ? `You're shown as ${persona.displayName}`
                    : `Pretend to be ${persona.displayName}`}
                </Button>
                <Text size="2" color="gray">
                  {persona.blurb}
                </Text>
              </Flex>
            );
          })}
          {anyPersonaActive ? (
            <Flex align="center" gap="3" className="mt-2">
              <Button
                data-testid="settings-demo-impersonate-clear"
                variant="outline"
                color="gray"
                onClick={clearPersona}
              >
                {t("settings.demoImpersonationCard.stopPretending")}
              </Button>
              <Text size="2" color="gray">
                {t("settings.demoImpersonationCard.switchBackRealNameAvatar")}
              </Text>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}
